import { useMemo } from 'react';
import { Box, Button, Column, Flex, Typography } from '@tempmee/react';
import { OutlineChevronLeftIcon } from '@tempmee/react-icons';
import defaultProfileImage from '~/images/defaultProfileImage.png';
import { colors } from '@tempmee/tailwind';
import { UserProfession } from '~/enums/UserProfession';
import { EducationUtils } from '~/utils/EducationUtils';
import { type TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';

interface ISkillsViewProps {
  fullName: string;
  profession: string;
  professionCode: string;
  education: TEducationMap;
  profileImage?: string;
  onBack: () => void;
}

const isDnDa = (code: string) => [UserProfession.DN, UserProfession.DA].includes(code);
const isRdhFd = (code: string) => [UserProfession.RDH, UserProfession.FD].includes(code);

const SCROLL_STYLE = {
  overflowY: 'auto' as const,
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${colors.neutral[200]} transparent`,
};

export const SkillsView = ({
  fullName,
  profession,
  professionCode,
  education,
  profileImage,
  onBack,
}: ISkillsViewProps) => {
  const safeEducation = education ?? {};
  const resolvedCode = safeEducation[professionCode]
    ? professionCode
    : (Object.keys(safeEducation)[0] ?? professionCode);

  const specialtiesAndProcedures = useMemo(
    () => safeEducation[resolvedCode]?.specialtiesAndProcedures ?? {},
    [safeEducation, resolvedCode],
  );

  const dnDaSpecialties = useMemo(() => {
    if (!isDnDa(resolvedCode)) return [];
    return Object.entries(specialtiesAndProcedures);
  }, [resolvedCode, specialtiesAndProcedures]);

  const rdhFdProcedures = useMemo(() => {
    if (!isRdhFd(resolvedCode)) return [];
    return EducationUtils.getProceduresOfProfession({
      specialtiesAndProcedures,
      profession: resolvedCode,
      specialties: [],
    }) as string[];
  }, [resolvedCode, specialtiesAndProcedures]);

  return (
    <Column
      gap={4}
      data-testid="embedded-skills-view"
      data-cy="embedded-skills-view"
      style={{ paddingRight: 24 }}
    >
      <Flex align="center" gap={3} style={{ minWidth: 0 }}>
        <Button
          variant="text"
          color="secondary"
          size="small"
          onClick={onBack}
          startIcon={<OutlineChevronLeftIcon />}
          data-testid="view-skills-back-button"
          data-cy="view-skills-back-button"
        >
          Back
        </Button>
        <Box
          as="span"
          role="img"
          aria-label="profile"
          style={{
            display: 'block',
            width: 32,
            height: 32,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundImage: `url(${profileImage || defaultProfileImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: colors.neutral[100],
          }}
        />
        <Flex align="center" gap={1} style={{ flex: 1, minWidth: 0 }}>
          <Typography
            bold
            variant="body1"
            color={colors.neutral[600]}
            numberOfLines={1}
            style={{ flexShrink: 1, minWidth: 0 }}
          >
            {fullName}
          </Typography>
          <Typography
            variant="body1"
            color={colors.neutral[600]}
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {` - ${profession}`}
          </Typography>
        </Flex>
      </Flex>

      <Column
        gap={4}
        style={{
          paddingLeft: 110,
          minHeight: 320,
          maxHeight: 600,
          ...SCROLL_STYLE,
        }}
      >
        {isDnDa(resolvedCode) &&
          dnDaSpecialties.length > 0 &&
          dnDaSpecialties.map(([specialtyKey, procedures]) => (
            <Column key={specialtyKey} gap={2} pl={3}>
              <Typography bold variant="body1" color={colors.primary[500]}>
                {EducationUtils.removeSpecialtyPrefix(specialtyKey)}
              </Typography>
              <Column gap={1} pl={3}>
                {procedures.map((proc: string) => (
                  <Typography key={proc} size="body_small" color={colors.neutral[600]}>
                    {`• ${EducationUtils.removeProcedurePrefix(proc)}`}
                  </Typography>
                ))}
              </Column>
            </Column>
          ))}

        {isRdhFd(resolvedCode) && rdhFdProcedures.length > 0 && (
          <Column gap={2} pl={3}>
            {rdhFdProcedures.map((name: string) => (
              <Typography key={name} size="body_small" color={colors.neutral[600]}>
                {`• ${EducationUtils.removeProcedurePrefix(name)}`}
              </Typography>
            ))}
          </Column>
        )}

        {!isDnDa(resolvedCode) && !isRdhFd(resolvedCode) && (
          <Column gap={2} pl={3}>
            <Typography size="body_small" italic color={colors.neutral[400]}>
              Skill details aren&apos;t available for this profession yet.
            </Typography>
          </Column>
        )}

        {((isDnDa(resolvedCode) && dnDaSpecialties.length === 0) ||
          (isRdhFd(resolvedCode) && rdhFdProcedures.length === 0)) && (
          <Column gap={2} pl={3}>
            <Typography size="body_small" italic color={colors.neutral[400]}>
              This professional hasn&apos;t listed any skills yet.
            </Typography>
          </Column>
        )}
      </Column>
    </Column>
  );
};
